/**
 * Cas Tiles — quote list drawer (localStorage + email draft).
 */
(function () {
  const LOCAL_STORAGE_KEY = 'eg_quote_v1';
  const PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/;
  const DEFAULT_MONEY_FORMAT = 'R {{amount}}';

  const init = () => {
    const drawer = document.querySelector('#eg-quote-drawer');
    if (!drawer || drawer.dataset.quoteInitialized === 'true') return;
    drawer.dataset.quoteInitialized = 'true';

    ensureBootstrap();

    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const resizeImage = (src, size, crop = '') => {
      if (!src || typeof src !== 'string') return '';
      return src
        .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
        .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => {
          const cropSuffix = crop.length ? `_crop_${crop}` : '';
          return `_${size}${cropSuffix}${match}`;
        });
    };

    const formatMoney = (cents, format) => {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const formatString = format || drawer.dataset.moneyFormat || DEFAULT_MONEY_FORMAT;

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (Number.isNaN(number) || number == null) return '0';
        const fixed = (number / 100.0).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const fraction = parts[1] ? decimal + parts[1] : '';
        return dollars + fraction;
      };

      const formatMatch = formatString.match(PLACEHOLDER_REGEX);
      if (!formatMatch) return formatWithDelimiters(cents, 2);

      let value;
      switch (formatMatch[1]) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return formatString.replace(PLACEHOLDER_REGEX, value);
    };

    const getPreferredVariant = (productJson) => {
      if (!productJson?.variants?.length) return null;
      return productJson.variants.find((variant) => variant.available) || productJson.variants[0];
    };

    const getQuoteList = () => {
      try {
        return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
      } catch (error) {
        console.error('Quote list parse error:', error);
        return [];
      }
    };

    const saveQuoteList = (quoteList) => {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(quoteList));
    };

    const syncLineNotesFromDrawer = () => {
      const noteFields = drawer.querySelectorAll('.quote-line-note');
      if (!noteFields.length) return getQuoteList();

      const notesByHandle = {};
      noteFields.forEach((field) => {
        notesByHandle[field.dataset.productHandle] = field.value.trim();
      });

      const updated = getQuoteList().map((item) => (
        notesByHandle[item.handle] !== undefined ? { ...item, note: notesByHandle[item.handle] } : item
      ));
      saveQuoteList(updated);
      return updated;
    };

    const updateLineItemNote = (handle, note) => {
      const updated = getQuoteList().map((item) => (
        item.handle === handle ? { ...item, note } : item
      ));
      saveQuoteList(updated);
    };

    const getProductHandle = (btn) => (
      btn?.dataset?.productHandle
      || btn?.closest('[data-product-handle]')?.dataset?.productHandle
      || ''
    );

    const openDrawer = () => {
      if (typeof bootstrap === 'undefined') return false;
      bootstrap.Offcanvas.getOrCreateInstance(drawer).show();
      return true;
    };

    const showQuoteDrawerWhenReady = async () => {
      buildQuoteDrawer();
      if (openDrawer()) return;
      await ensureBootstrap();
      openDrawer();
    };

    drawer.addEventListener('show.bs.offcanvas', () => {
      buildQuoteDrawer();
      updateQuoteBadges();
    });

    const releaseDrawerFocus = () => {
      const activeEl = document.activeElement;
      if (activeEl instanceof HTMLElement && drawer.contains(activeEl)) {
        activeEl.blur();
      }
    };

    drawer.addEventListener('hide.bs.offcanvas', releaseDrawerFocus);

    const updateQuoteBadges = () => {
      const quoteList = getQuoteList();
      const totalItems = quoteList.reduce((sum, item) => sum + (item.quantity || 1), 0);

      document.querySelectorAll('[data-quote-count-bubble]').forEach((bubble) => {
        const textEl = bubble.querySelector('.text');
        const count = totalItems < 100 ? totalItems : totalItems;

        bubble.style.display = 'flex';
        bubble.setAttribute('aria-hidden', 'false');
        if (textEl) textEl.textContent = count;
        else bubble.textContent = count;
      });

      document.querySelectorAll('.eg-quote-toggle-btn').forEach((btn) => {
        const productHandle = getProductHandle(btn);
        if (!productHandle) return;
        const isQuoted = quoteList.some((item) => item.handle === productHandle);
        btn.classList.toggle('quote-added', isQuoted);
        btn.setAttribute('aria-pressed', isQuoted ? 'true' : 'false');
        btn.setAttribute('aria-label', isQuoted ? drawer.dataset.textRemove : drawer.dataset.textAdd);
      });
    };

    const initQuoteButtons = () => {
      document.querySelectorAll('.eg-quote-toggle-btn').forEach((btn) => {
        const addSpan = btn.querySelector('[data-add]');
        const removeSpan = btn.querySelector('[data-remove]');
        if (addSpan && drawer.dataset.textAdd) addSpan.textContent = drawer.dataset.textAdd;
        if (removeSpan && drawer.dataset.textRemove) removeSpan.textContent = drawer.dataset.textRemove;
      });
    };

    const buildQuoteDrawer = () => {
      try {
        const quoteList = getQuoteList();
        const drawerEmpty = drawer.querySelector('#eg-quote-drawer-empty');
        const drawerProductList = drawer.querySelector('#eg-quote-drawer-product-list');
        const drawerFooter = drawer.querySelector('#eg-quote-drawer-footer');

        if (!drawerEmpty || !drawerProductList) return;

        drawerProductList.innerHTML = '';

        if (quoteList.length === 0) {
          drawerEmpty.style.display = 'block';
          drawerProductList.style.display = 'none';
          if (drawerFooter) drawerFooter.style.display = 'none';
          return;
        }

        let itemsHtml = '';
        quoteList.forEach((product) => {
          const qty = product.quantity || 1;
          const title = escapeHtml(product.title || '');
          const url = escapeHtml(product.url || '#');
          const handle = escapeHtml(product.handle || '');
          const sku = product.sku ? escapeHtml(product.sku) : '';
          const lineNote = escapeHtml(product.note || '');
          const lineNoteLabel = escapeHtml(drawer.dataset.labelLineNote || 'Note');
          const lineNotePlaceholder = escapeHtml(drawer.dataset.placeholderLineNote || 'Add a note for this product (optional)');
          const image = resizeImage(
            product.featured_image || '',
            `${drawer.dataset.imgWidth}x${drawer.dataset.imgHeight}`,
            'center'
          );

          itemsHtml += `
            <li class="product-list-item">
              <div class="quote-item-main mx-n3 py-3">
                <div class="d-flex align-items-start quote-item-top">
                  <a href="${url}" class="quote-item-image-link d-block me-3">
                    <img
                      class="quote-item-image"
                      src="${image}"
                      alt=""
                      width="${drawer.dataset.imgWidth}"
                      height="${drawer.dataset.imgHeight}"
                      loading="lazy">
                  </a>
                  <div class="w-100 min-width-0">
                    <a href="${url}" class="cart-item__name break mb-1">${title}</a>
                    ${sku ? `<div class="small text-muted mb-1">SKU: ${sku}</div>` : ''}
                    <div class="product-price-container product-option mb-1">
                      <span class="product-price-final">${formatMoney(product.price)}</span>
                    </div>
                    <div class="quote-qty-control" data-product-handle="${handle}">
                      <button type="button" class="btn-quote-qty-minus" aria-label="Decrease quantity">−</button>
                      <span class="quote-qty-value">${qty}</span>
                      <button type="button" class="btn-quote-qty-plus" aria-label="Increase quantity">+</button>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="btn-remove-from-quote ms-2"
                    aria-label="${drawer.dataset.textRemove}"
                    data-product-handle="${handle}">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 2l8 8M10 2 2 10"/></svg>
                  </button>
                </div>
                <div class="quote-line-note-wrap">
                  <textarea
                    id="quote-note-${handle}"
                    class="quote-line-note"
                    rows="2"
                    placeholder="${lineNotePlaceholder}"
                    aria-label="${lineNoteLabel}"
                    data-product-handle="${handle}">${lineNote}</textarea>
                </div>
              </div>
            </li>`;
        });

        drawerProductList.insertAdjacentHTML('afterbegin', itemsHtml);
        drawerEmpty.style.display = 'none';
        drawerProductList.style.display = 'block';
        if (drawerFooter) drawerFooter.style.display = 'block';

        drawerProductList.querySelectorAll('.btn-remove-from-quote:not(.init)').forEach((btn) => {
          btn.classList.add('init');
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            const updated = getQuoteList().filter((item) => item.handle !== btn.dataset.productHandle);
            saveQuoteList(updated);
            updateQuoteBadges();
            buildQuoteDrawer();
          });
        });

        drawerProductList.querySelectorAll('.quote-qty-control:not(.init)').forEach((control) => {
          control.classList.add('init');
          const handle = control.dataset.productHandle;
          const minusBtn = control.querySelector('.btn-quote-qty-minus');
          const plusBtn = control.querySelector('.btn-quote-qty-plus');
          const valueEl = control.querySelector('.quote-qty-value');

          const setQty = (newQty) => {
            const updated = getQuoteList().map((item) => (
              item.handle === handle ? { ...item, quantity: newQty } : item
            ));
            saveQuoteList(updated);
            valueEl.textContent = newQty;
            updateQuoteBadges();
          };

          minusBtn.addEventListener('click', () => {
            const current = parseInt(valueEl.textContent, 10) || 1;
            if (current <= 1) {
              saveQuoteList(getQuoteList().filter((item) => item.handle !== handle));
              updateQuoteBadges();
              buildQuoteDrawer();
            } else {
              setQty(current - 1);
            }
          });

          plusBtn.addEventListener('click', () => {
            setQty((parseInt(valueEl.textContent, 10) || 1) + 1);
          });
        });

        drawerProductList.querySelectorAll('.quote-line-note:not(.init)').forEach((field) => {
          field.classList.add('init');
          const saveNote = () => {
            updateLineItemNote(field.dataset.productHandle, field.value.trim());
          };

          field.addEventListener('blur', saveNote);
          field.addEventListener('change', saveNote);
        });
      } catch (error) {
        console.error('Quote drawer build error:', error);
      }
    };

    const handleQuoteButtonClick = async (btn, event) => {
      event.preventDefault();
      event.stopPropagation();

      const productHandle = getProductHandle(btn);
      if (!productHandle) {
        console.error('Quote button: missing product handle');
        return;
      }

      try {
        let quoteList = getQuoteList();
        const existing = quoteList.find((item) => item.handle === productHandle);

        if (existing) {
          quoteList = quoteList.filter((item) => item.handle !== productHandle);
        } else {
          const response = await fetch(`/products/${productHandle}.js`);
          if (!response.ok) {
            console.error('Quote button: product fetch failed', productHandle, response.status);
            return;
          }

          const product = await response.json();
          if (!product?.id) {
            console.error('Quote button: invalid product response', productHandle);
            return;
          }

          const variant = getPreferredVariant(product);
          quoteList.push({
            id: product.id,
            handle: productHandle,
            variant_id: variant?.id || null,
            sku: variant?.sku || '',
            url: product.url,
            title: product.title.split(' _ ')[0],
            price: product.price,
            featured_image: product.featured_image,
            vendor: product.vendor,
            quantity: 1,
            note: '',
            time: Date.now(),
          });

          showQuoteDrawerWhenReady();
        }

        saveQuoteList(quoteList);
        updateQuoteBadges();
        buildQuoteDrawer();
      } catch (error) {
        console.error('Quote button error:', error);
      }
    };

    const isValidEmail = (email) => {
      const input = document.createElement('input');
      input.type = 'email';
      input.required = true;
      input.value = email;
      return input.checkValidity();
    };

    const buildPlainTextQuoteBody = ({
      shopName,
      emailSubject,
      emailIntro,
      customerName,
      customerEmail,
      customerPhone,
      customerMessage,
      quoteList,
      siteUrl,
    }) => {
      let productLines = '';
      quoteList.forEach((product, index) => {
        const qty = product.quantity || 1;
        const linePrice = formatMoney(product.price);
        const productUrl = `${siteUrl}${product.url}`;
        productLines += `${index + 1}. ${product.title}`;
        if (product.sku) productLines += ` (SKU: ${product.sku})`;
        productLines += `\n   Qty: ${qty} | Price: ${linePrice}\n   ${productUrl}`;
        if (product.note?.trim()) {
          productLines += `\n   ${drawer.dataset.labelLineNote || 'Note'}: ${product.note.trim()}`;
        }
        productLines += '\n\n';
      });

      return [
        shopName,
        '',
        emailIntro,
        '',
        emailSubject,
        '='.repeat(emailSubject.length),
        '',
        'Customer details',
        `${drawer.dataset.labelName}: ${customerName}`,
        `${drawer.dataset.labelEmail}: ${customerEmail}`,
        `${drawer.dataset.labelPhone}: ${customerPhone || '—'}`,
        `${drawer.dataset.labelMessage}: ${customerMessage || '—'}`,
        '',
        'Requested products',
        '',
        productLines.trimEnd(),
      ].join('\n');
    };

    const buildMailtoUrl = ({ merchantEmail, customerEmail, emailSubject, mailBody }) => {
      const query = [
        `cc=${encodeURIComponent(customerEmail)}`,
        `subject=${encodeURIComponent(emailSubject)}`,
        `body=${encodeURIComponent(mailBody)}`,
      ].join('&');

      return `mailto:${merchantEmail}?${query}`;
    };

    const openQuoteEmail = ({
      merchantEmail,
      customerEmail,
      emailSubject,
      mailBody,
    }) => {
      const mailtoBodyMax = 1800;
      let body = mailBody;

      if (body.length > mailtoBodyMax) {
        body = `${body.slice(0, mailtoBodyMax)}\n\n[Quote list truncated due to email length limits.]`;
      }

      releaseDrawerFocus();
      window.location.href = buildMailtoUrl({
        merchantEmail,
        customerEmail,
        emailSubject,
        mailBody: body,
      });

      return {
        message: drawer.dataset.textSuccessFallback || drawer.dataset.textSuccess,
      };
    };

    const sendQuoteRequest = async (form) => {
      const quoteList = getQuoteList();
      const messageEl = drawer.querySelector('#eg-quote-form-message');
      const submitBtn = form.querySelector('.btn-send-quote');

      const customerName = form.querySelector('[name="quoteCustomerName"]')?.value.trim() || '';
      const customerEmail = form.querySelector('[name="quoteCustomerEmail"]')?.value.trim() || '';
      const customerPhone = form.querySelector('[name="quoteCustomerPhone"]')?.value.trim() || '';
      const customerMessage = form.querySelector('[name="quoteCustomerMessage"]')?.value.trim() || '';

      let validationMessage = '';
      if (quoteList.length === 0) {
        validationMessage = drawer.dataset.textErrorEmpty || drawer.dataset.textError;
      } else if (!customerName) {
        validationMessage = drawer.dataset.textErrorName || drawer.dataset.textError;
      } else if (!customerEmail || !isValidEmail(customerEmail)) {
        validationMessage = drawer.dataset.textErrorEmail || drawer.dataset.textError;
      }

      if (validationMessage) {
        messageEl.innerHTML = `<div class="alertBox alertBox--error"><p class="alertBox-message">${validationMessage}</p></div>`;
        return;
      }

      submitBtn.disabled = true;
      const originalText = submitBtn.textContent;
      submitBtn.textContent = drawer.dataset.textSending || 'Preparing email...';
      messageEl.innerHTML = '';
      releaseDrawerFocus();

      const resetSubmitButton = () => {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      };

      const siteUrl = window.location.origin;
      const emailSubject = drawer.dataset.emailSubject;
      const shopName = drawer.dataset.shopName || 'Cas Tiles';
      const quoteListWithNotes = syncLineNotesFromDrawer();
      const plainTextBody = buildPlainTextQuoteBody({
        shopName,
        emailSubject,
        emailIntro: drawer.dataset.textEmailIntro || 'Please find my quote request below.',
        customerName,
        customerEmail,
        customerPhone,
        customerMessage,
        quoteList: quoteListWithNotes,
        siteUrl,
      });

      try {
        const result = openQuoteEmail({
          merchantEmail: drawer.dataset.merchantEmail,
          customerEmail,
          emailSubject,
          mailBody: plainTextBody,
        });

        messageEl.innerHTML = `<div class="alertBox alertBox--success"><p class="alertBox-message">${result.message}</p></div>`;
        saveQuoteList([]);
        form.reset();
        updateQuoteBadges();
        buildQuoteDrawer();
      } catch (error) {
        console.error('Quote email error:', error);
        messageEl.innerHTML = `<div class="alertBox alertBox--error"><p class="alertBox-message">${drawer.dataset.textNetworkError || drawer.dataset.textError}${error?.message ? `<br><small>${escapeHtml(error.message)}</small>` : ''}</p></div>`;
      } finally {
        resetSubmitButton();
      }
    };

    window.onClickEgQuoteButton = handleQuoteButtonClick;
    window.egQuoteRefresh = () => {
      updateQuoteBadges();
      buildQuoteDrawer();
    };

    const openQuoteDrawerFromTrigger = (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      showQuoteDrawerWhenReady();
    };

    document.querySelectorAll('#quote-icon-bubble, [href="#eg-quote-drawer"][data-bs-toggle="offcanvas"], [data-bs-target="#eg-quote-drawer"]').forEach((trigger) => {
      if (trigger.classList.contains('eg-quote-toggle-btn')) return;
      trigger.addEventListener('click', openQuoteDrawerFromTrigger);
    });

    document.addEventListener('click', (event) => {
      const quoteBtn = event.target.closest('.eg-quote-toggle-btn');
      if (quoteBtn) {
        handleQuoteButtonClick(quoteBtn, event);
      }
    });

    const quoteForm = drawer.querySelector('#eg-quote-request-form');
    if (quoteForm) {
      quoteForm.addEventListener('submit', (event) => {
        event.preventDefault();
        sendQuoteRequest(quoteForm);
      });
    }

    if (window.location.hash.includes('#quote')) {
      window.setTimeout(showQuoteDrawerWhenReady, 200);
    }

    updateQuoteBadges();
    initQuoteButtons();
    buildQuoteDrawer();
    ensureBootstrap();
  };

  function ensureBootstrap() {
    if (window.bootstrap) return Promise.resolve(window.bootstrap);

    const existing = document.querySelector('#bootstrap-js');
    if (existing) {
      return new Promise((resolve, reject) => {
        if (window.bootstrap) {
          resolve(window.bootstrap);
          return;
        }
        existing.addEventListener('load', () => resolve(window.bootstrap), { once: true });
        existing.addEventListener('error', () => reject(new Error('Bootstrap failed to load')), { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'bootstrap-js';
      script.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.min.js';
      script.crossOrigin = 'anonymous';
      script.onload = () => resolve(window.bootstrap);
      script.onerror = () => reject(new Error('Bootstrap failed to load'));
      document.head.appendChild(script);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
